/**
 * PrivacyScreen — "Download my data" + "Delete my account".
 * STORY-001-010 Implementation logic §4 + AC #6, #7, #8, #9.
 *
 * Export (AC #6/#7): `GET /me/export` returns a ZIP. The access token lives in
 * JS memory (REQ-PRD-§11.2-A — never localStorage, never a readable cookie), so
 * a plain `<a download>` cannot carry the Authorization header; the shared
 * `downloadMyData()` in `features/account/api.ts` fetches the blob with the
 * Bearer header and triggers an object-URL download. A 429 surfaces the
 * rate-limited message.
 *
 * Delete (AC #8/#9): the typed-phrase `DeleteAccountDialog`. On a successful
 * 202 we clear in-memory auth and redirect to /login (the server has already
 * revoked every session).
 *
 * THE 2026-08 REBUILD
 *
 * Before: two bordered boxes — one with a decorative motif behind it — each
 * carrying a heading, a grey sentence and a button. The export said "Архив ZIP
 * с вашими данными в формате JSON", which tells a first-time user nothing about
 * what is actually inside, and the export was a blocking spinner on a button
 * with no sense of progress.
 *
 * After: a ruled slip whose stub says what the archive IS, and a ruled MANIFEST
 * of what the archive contains — the same anatomy as a counter slip listing
 * line items. The export runs through `notify.promise`, which is what a
 * long-running server job deserves: one toast carrying loading → done → failed,
 * so the user can keep reading while it builds. Deletion sits in its own danger
 * sheet, well below, behind the typed phrase.
 *
 * Test ids (`privacy-screen`, `privacy-export`, `privacy-delete`) and the
 * export/delete behaviour are unchanged — `account-export.spec.ts`,
 * `account-delete.spec.ts` and `PrivacyScreen.test.tsx` gate them.
 */
import { useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { Stagger, StaggerItem } from "@/components/motion";
import {
  DangerZone,
  Slip,
  SlipRow,
  SlipSection,
} from "@/features/account/components/Slip";
import { DeleteAccountDialog } from "@/features/account/components/DeleteAccountDialog";
import { deleteAccount, downloadMyData, registerAccountI18n } from "@/features/account/api";
import { endLocalSession } from "@/features/chat/session-end";
import { ApiError } from "@/lib/api-client";
import { useAuthStore } from "@/lib/auth-store";
import { notify } from "@/lib/notify";
import { queryClient } from "@/lib/queryClient";

/** What the export archive actually contains — stated, not summarised. */
const MANIFEST_KEYS = [
  "privacy.manifest.profile",
  "privacy.manifest.threads",
  "privacy.manifest.events",
] as const;

export function PrivacyScreen(): ReactElement {
  registerAccountI18n();
  const { t } = useTranslation("account");
  const navigate = useNavigate();
  const clearAuth = useAuthStore((s) => s.clearAuth);

  const [exporting, setExporting] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  /**
   * A server-side ZIP build is exactly the case `notify.promise` exists for:
   * one toast that carries its own state instead of a spinner the user has to
   * sit and watch. `downloadMyData` resolves an outcome rather than throwing,
   * so it is re-thrown here to drive the toast's error branch by name.
   */
  const onExport = (): void => {
    if (exporting) return;
    setExporting(true);
    const run = downloadMyData().then((outcome) => {
      if (outcome !== "ok") throw new Error(outcome);
      return outcome;
    });
    notify.promise(run, {
      loading: t("privacy.exporting"),
      success: t("privacy.exportReady"),
      error: (err) =>
        err instanceof Error && err.message === "rate_limited"
          ? t("privacy.exportRateLimited")
          : t("privacy.exportError"),
    });
    void run.catch(() => undefined).finally(() => setExporting(false));
  };

  const onConfirmDelete = async (confirmPhrase: string): Promise<boolean> => {
    try {
      await deleteAccount(confirmPhrase);
      // The server revoked every session; clear in-memory auth + redirect.
      clearAuth();
      // W0.3: same reasoning as logout — the query cache and the chat store
      // must not survive this session ending either. The navigate() below is
      // an SPA transition, not a reload, so without this the previous
      // person's open thread would still be sitting in this tab for whoever
      // uses this device next.
      queryClient.clear();
      endLocalSession();
      notify.success(t("delete.scheduledToast"), {
        description: t("delete.scheduledNote"),
      });
      navigate("/login", { replace: true });
      return true;
    } catch (err) {
      // A 422 (confirm phrase invalid) is handled by the dialog's own phrase
      // check; anything else is a real failure the user should hear by name.
      if (err instanceof ApiError && err.code === "account.delete.confirm_phrase_invalid") {
        return false;
      }
      notify.error(t("delete.failed"), {
        description: t("errors.generic"),
        id: "account-delete",
      });
      return false;
    }
  };

  return (
    <Stagger className="flex flex-col" data-testid="privacy-screen">
      <StaggerItem>
        <Slip>
          <SlipSection
            caption={t("privacy.exportTitle")}
            note={t("privacy.exportDescription")}
            headingAs="h2"
          >
            {/* The manifest — what is in the archive, as line items. */}
            <ul className="g-ruled">
              {MANIFEST_KEYS.map((key) => (
                <li
                  key={key}
                  className="px-4 py-3 text-[13.5px] leading-snug text-[var(--g-ink-2)] md:px-5"
                >
                  {t(key)}
                </li>
              ))}
            </ul>
            <SlipRow
              testId="privacy-export"
              onClick={onExport}
              hideChevron
              disabled={exporting}
              ariaLabel={t("privacy.exportButton")}
              label={exporting ? t("privacy.exporting") : t("privacy.exportButton")}
              hint={t("privacy.exportHint")}
              value="ZIP"
              valueAsData
            />
          </SlipSection>
        </Slip>
      </StaggerItem>

      <StaggerItem>
        <DangerZone
          title={t("privacy.dangerSection")}
          note={t("privacy.deleteDescription")}
          testId="privacy-danger"
        >
          <SlipRow
            testId="privacy-delete"
            onClick={() => setDeleteOpen(true)}
            hideChevron
            danger
            label={t("privacy.deleteButton")}
            hint={t("privacy.deleteHint")}
          />
        </DangerZone>
      </StaggerItem>

      <DeleteAccountDialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={onConfirmDelete}
      />
    </Stagger>
  );
}

export default PrivacyScreen;
